import type React from "react";
import type { TournamentDetailResponse } from "../../../@apis/dto/response/torunament.response.ts";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore.tsx";
import { parseBirth, getAgeType } from "../../../lib/utils/birthdayUtils.ts";
import { formatDate } from "../../../lib/utils/dateUtils.ts";
import { REGION_OPTIONS } from "../../../lib/constants/region.constants.ts";
import Button from "../button";
import BottomDrawer from "./index.tsx";

interface ConfirmDrawerProps {
  open: boolean;
  onClose: () => void;
  item: TournamentDetailResponse;
}

const regionLabel = (value?: string) =>
  REGION_OPTIONS.find((r) => r.value === value)?.label ?? value ?? "-";

// 라벨/값 한 줄
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-[8px] items-start">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      <p className="flex-1 min-w-0 text-black">{value}</p>
    </div>
  );
}

export default function ConfirmDrawer({
  open,
  onClose,
  item,
}: ConfirmDrawerProps) {
  const { myInfo } = useMyInfoStore();
  const info = item.tournamentInfo;
  const apply = info.apply;

  const isCollectRegion = info.isCollectRegion === true;
  const isCertificateAccept = info.isCertificateAccept !== false;
  const hasClass = info.hasClass ?? false;

  return (
    <BottomDrawer open={open} onClose={onClose}>
      <div className="flex flex-col items-center gap-[12px]">
        {/* 상태 */}
        <div className="bg-[#ff3b30] size-10 rounded-full flex items-center justify-center">
          <img className="w-5 h-3.5" src="/icons/ic-check.webp" />
        </div>
        <p className="text-xl font-medium text-black">접수완료</p>

        <div className="w-full h-px bg-[#e5e5ea]" />

        <div className="w-full flex flex-col gap-[8px]">
          <p className="text-base font-semibold text-black break-keep">
            {info.name}
          </p>

          {/* 접수번호 */}
          <div className="bg-[#f2f2f7] rounded-[10px] p-[12px] flex items-center justify-between text-sm font-semibold">
            <p className="text-black">접수번호</p>
            <p className="text-[#ff3b30]">
              {String(apply?.id ?? "").padStart(6, "0")}
            </p>
          </div>

          {/* 대회 정보 */}
          <div className="bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[4px] text-sm font-medium">
            <Row label="대회명" value={info.name} />
            <Row
              label="대회날짜"
              value={formatDate(
                info.calendarInfo?.date || "",
                "YYYY-MM-DD (ddd)",
              )}
            />
            <Row label="대회일정" value={info.calendarInfo?.timeType || "-"} />
            <Row label="대회장소" value={info.golfParkName || "-"} />
          </div>

          {/* 내 신청 정보 (대회 설정에서 활성화한 항목만 노출) */}
          <div className="bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[4px] text-sm font-medium">
            <Row label="이름" value={myInfo.name} />
            <Row
              label="생년월일"
              value={parseBirth(myInfo.birthdate).format("YYYY년 M월 D일")}
            />
            <Row label="성별" value={myInfo.gender == "0" ? "여성" : "남성"} />
            <Row label="이메일" value={apply?.email || "-"} />
            {isCollectRegion && (
              <Row label="지역" value={regionLabel(apply?.region)} />
            )}
            {isCertificateAccept && (
              <Row
                label="프로자격증"
                value={apply?.haveProCertificate ? "보유" : "미보유"}
              />
            )}
            {hasClass && (
              <Row
                label="클래스"
                value={`${getAgeType(myInfo.birthdate)} 클래스`}
              />
            )}
          </div>

          {/* 안내 (certificateHtml 있을 때만 노출) */}
          {info.certificateHtml && (
            <div className="bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[12px]">
              <p className="text-sm font-semibold text-black break-keep">
                귀하의 참가 신청이 정상적으로 완료되었습니다.
                <br />
                다음 사항을 반드시 확인해 주시기 바랍니다.
              </p>
              <div className="w-full h-px bg-[#d1d1d6]" />
              <div
                className="text-sm font-medium text-black break-keep [&_img]:max-w-full"
                dangerouslySetInnerHTML={{ __html: info.certificateHtml }}
              />
            </div>
          )}
        </div>

        <Button
          className="w-full"
          variant="white"
          onClick={onClose}
          label="닫기"
        />
      </div>
    </BottomDrawer>
  );
}
